import {CheckCircle, MapPin, User, CreditCard, Wallet, UserPlus, Users, BadgeCheck} from "lucide-react";
import { PERSONAL_DETAILS } from "./personalDetails";
import { FieldType } from "../../../components/ui/DynamicInput";
import { APPLICATION_DETAILS } from "./applicationDetails";
import { KYC_DETAILS } from "./kycDetails";
import {INDIAN_STATES } from "./indiaStatesList";
import { LOAN_REQUIREMENT_DETAILS } from "./loanRequirementDetails";
import {INCOME_DETAILS_FIELDS} from "./incomeDetails";
import {MEMBERSHIP_DETAILS_FIELDS} from "./membershipDetails";

export interface FormFields {
  fields: FieldType[];
}

export interface loanFormFieldType {
  sectionDescription?: string;
  section: string;
  name: string;
  fields: FieldType[];
  icon: React.ComponentType;
  isCustomComponent?: boolean;
  customComponentName?: string;
}

// Form config to make form filds dynamic
// It will help update sequence of form field anytime by changing order in formSections array
// Or to add and remove any field
export const loanFormFields: loanFormFieldType[] = [
  {
    sectionDescription: "Application Id, Product Type",
    section: "Application Details",
    name: "applicationDetails",
    fields: APPLICATION_DETAILS.fields,
    icon: User,
  },
  {
    sectionDescription: "Upload PAN, Aadhar etc...",
    section: "KYC Details",
    name: "kycDetails",
    fields: KYC_DETAILS.fields,
    icon: CheckCircle,
  },
  {
    icon: User,
    section: "Personal Details",
    name: "personalDetails",
    fields: PERSONAL_DETAILS.fields,
  },
  {
    icon: User,
    section: "Loan Requirement Details",
    name: "loanRequirementDetails",
    fields: LOAN_REQUIREMENT_DETAILS.fields,
  },
  {
    icon: Wallet,
    section: "Income Details",
    name: "incomeDetails",
    fields: INCOME_DETAILS_FIELDS.fields
  },
  {
    sectionDescription: "Add one or more addresses",
    section: "Address Details",
    name: "addressDetails",
    fields: [],
    icon: MapPin,
    isCustomComponent: true,
    customComponentName: "AddressManager"
  },
  {
    sectionDescription: "Add co-applicant details",
    section: "Co-Applicant Details",
    name: "coApplicantDetails",
    fields: [],
    icon: Users,
    isCustomComponent: true,
    customComponentName: "CoApplicantManager"
  },
  {
    sectionDescription: "Add one or more references",
    section: "Reference Details",
    name: "referenceDetails",
    fields: [],
    icon: UserPlus,
    isCustomComponent: true,
    customComponentName: "ReferenceManager"
  },
  {
    sectionDescription: "Add one or more bank accounts",
    section: "Bank Account Details",
    name: "bankDetails",
    fields: [],
    icon: CreditCard,
    isCustomComponent: true,
    customComponentName: "BankManager"
  },
  {
    sectionDescription: "Add membership details",
    section: "PIL Member Details",
    name: "membershipDetails",
    fields: MEMBERSHIP_DETAILS_FIELDS.fields,
    icon: BadgeCheck,
  },
  {
    sectionDescription: "Upload required documents",
    section: "Documents",
    name: "documents",
    icon: CheckCircle,
    fields: [],
  },
  {
    sectionDescription: "Review and submit",
    section: "Review and submit",
    name: "review",
    icon: CheckCircle,
    fields: [],
  },
];

export const LEADS_FORM_FIELDS: FieldType[] = [
  {
    name: "firstName",
    label: "First Name",
    type: "text",
    validationRules: { required: "First name is required" },
  },
  {
    name: "middleName",
    label: "Middle Name",
    type: "text",
    validationRules: { required: false },
  },
  { 
    name: "lastName",
    label: "Last Name",
    type: "text",
    validationRules: { required: "Last name is required" },
  },
  {
    name: "phoneNumber",
    label: "Mobile No.",
    type: "text",
    validationRules: {
      required: "Mobile number is required", // Field is required
      pattern: {
        value: /^(?!0)\d{10}$/, // Ensures the number does not start with 0 and is 10-15 digits long
        message:
          "Mobile number must be valid with 10 digits and cannot start with 0", // Error message for invalid number
      },
      errorMessage: "Mobile number must be valid with 10 to 15 digits", // General error message
    },
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    validationRules: {
      required: false,
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Enter a valid email address",
      },
    },
  },
  {
    name: "state",
    label: "State",
    type: "select",
    validationRules: { required: "State is required" },
    options: INDIAN_STATES,
  },
  {
    name: "city",
    label: "City",
    type: "select",
    options: [],
    validationRules: { required: "City is required" },
  },
  {
    name: "addressLineOne",
    label: "Address Line 1",
    type: "text",
    validationRules: {
      required: false,
    },
  },
  {
    name: "addressLineTwo",
    label: "Address Line 2",
    type: "text",
    validationRules: { required: false },
  },
  {
    name: "postalCode",
    label: "Postal Code",
    type: "text",
    validationRules: {
      required: false,
      pattern: {
        value: /^\d{6}$/,
        message: "Postal code must be 6 digits",
      },
      errorMessage: "Postal code must be 6 digits",
    },
  },
  {
    name: "loanTenure",
    label: "Loan tenure",
    type: "tel",
    validationRules: { required: false },
  },
  {
    name: "loanAmount",
    label: "Loan amount",
    type: "tel",
    validationRules: { required: false,
      pattern: {
        value: /^(0|[1-9]\d*)$/,
        message: "Amount must be a valid positive number",
      },
      maxLength: {
        value: 7,
        message: "Amount must be at most 7 digits", 
      },
    },
  },
  {
    name: "roi",
    label: "ROI",
    type: "tel",
    validationRules: { required: false },
  },
  {
    name: "referrer",
    label: "Referrer",
    type: "text",
    validationRules: { required: "Referrer is required" },
  },
  {
    name: "pilId",
    label: "PIL Id",
    type: "text",
    validationRules: {
      required: false,
      pattern: {
        value: /^[a-zA-Z0-9]{5,20}$/,
        message: "PIL Id must be between 5 to 20 characters",
      }
    }
  },
  {
    name: "channel",
    label: "Sourcing Channel",
    type: "select",
    validationRules: { required: "Sourcing channel is required" },
    options: [
      "PIL BD",
      "PIL Sales",
      "PIL Contractor",
      "Dealer",
      "Website",
      "Existing Customer",
      "WhatsApp",
      "FCC",
      "Other",
    ],
  },
];
